import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

export const FAQPage = () => {
  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "How does Yalla ! Lets Save. work?",
          a: "Simply browse our curated deals across categories and locations, save your favorites, and redeem them at participating merchants. All deals are verified and updated daily."
        },
        {
          q: "Is it really free to use?",
          a: "Yes! There are no subscription fees, hidden charges, or membership costs. Browse and use deals completely free."
        },
        {
          q: "Do I need to create an account?",
          a: "You can browse deals without an account, but creating one (free) lets you save deals, track your savings, and get personalized recommendations."
        }
      ]
    },
    {
      category: "Finding & Using Deals",
      questions: [
        {
          q: "How do I find deals near me?",
          a: "Use our location filter to select your emirate or neighborhood. You can also use the search function to find specific types of deals."
        },
        {
          q: "How do I redeem a deal?",
          a: "Each deal has specific redemption instructions. Common methods include: showing the deal on your phone, mentioning a promo code, booking online, or presenting a printed voucher."
        },
        {
          q: "Can I use multiple deals at once?",
          a: "This depends on the merchant's terms. Check each deal's conditions - some allow stacking, others don't."
        },
        {
          q: "What if a merchant doesn't honor the deal?",
          a: "Contact us immediately at hello@yallasavings.com with details. We verify all deals and work with merchants to resolve any issues."
        }
      ]
    },
    {
      category: "Deals & Validity",
      questions: [
        {
          q: "How often are deals updated?",
          a: "We update deals daily. Expired deals are automatically removed, and new ones are added throughout the day."
        },
        {
          q: "Why did a deal disappear?",
          a: "Deals have expiry dates or limited quantities. When they expire or sell out, they're removed from the site."
        },
        {
          q: "Can I suggest a deal or merchant?",
          a: "Absolutely! Email us at hello@yallasavings.com or use the contact form. We're always looking for great deals to feature."
        },
        {
          q: "Are all deals available in all emirates?",
          a: "No, deals are location-specific. Use our emirate filter to see what's available in your area."
        }
      ]
    },
    {
      category: "Account & Savings",
      questions: [
        {
          q: "How do you track my savings?",
          a: "When you save a deal and click 'Get Deal', we calculate the potential savings based on the discount. Your dashboard shows total savings."
        },
        {
          q: "Can I share deals with friends?",
          a: "Yes! Every deal has share buttons for WhatsApp, Facebook, Instagram, and email."
        },
        {
          q: "How do I change my email or password?",
          a: "Go to your Profile settings (click your name in top right) and update your information there."
        },
        {
          q: "How do I delete my account?",
          a: "Email us at hello@yallasavings.com with your request. We'll process it within 48 hours."
        }
      ]
    },
    {
      category: "For Merchants",
      questions: [
        {
          q: "How can my business be featured?",
          a: "Visit our Merchant Partnership page or email merchants@yallasavings.com. We offer free and premium listing options."
        },
        {
          q: "What are the benefits of listing deals?",
          a: "Reach thousands of UAE residents actively looking for deals, fill slow hours/days, attract new customers, and only pay for performance."
        },
        {
          q: "How much does it cost?",
          a: "We offer free basic listings. Premium options (featured placement, homepage spots) start at AED 500/month per deal."
        }
      ]
    },
    {
      category: "Technical Issues",
      questions: [
        {
          q: "The website isn't loading properly",
          a: "Try clearing your browser cache, using a different browser, or check your internet connection. If issues persist, contact us."
        },
        {
          q: "I can't log in to my account",
          a: "Use the 'Forgot Password' link on the login page. If that doesn't work, email hello@yallasavings.com for help."
        },
        {
          q: "Deal images aren't showing",
          a: "This is usually a temporary loading issue. Refresh the page or try again in a few minutes."
        },
        {
          q: "Can I use the site on my phone?",
          a: "Yes! Our website is fully mobile-optimized and works on all devices."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4" data-testid="faq-heading">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-600">
              Find answers to common questions about Yalla ! Lets Save.
            </p>
          </div>

          {/* FAQ Sections */}
          <div className="space-y-8">
            {faqs.map((section, idx) => (
              <Card key={idx}>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-4 text-emerald-600">
                    {section.category}
                  </h2>
                  <Accordion type="single" collapsible className="w-full">
                    {section.questions.map((faq, qIdx) => (
                      <AccordionItem key={qIdx} value={`item-${idx}-${qIdx}`}>
                        <AccordionTrigger className="text-left">
                          {faq.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-gray-600">
                          {faq.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact CTA */}
          <Card className="mt-12 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold mb-3">Still have questions?</h3>
              <p className="mb-6 text-emerald-50">
                Can't find what you're looking for? We're here to help!
              </p>
              <a
                href="/contact"
                className="inline-block bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Contact Us
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};